import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import {
    Alert,
    Button,
    Chip,
    Grid,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { getApiBaseUrl } from "../../config/api";
import { getMalawiGrade } from "../../utils/gradingUtils";
import {
    classPassRuleSummary,
    evaluateTermClassPass,
} from "../../utils/classPassEvaluation";
import {
    ResultsPageContainer,
    ResultsPageHeader,
    KpiCard,
    ResultsSectionPaper,
    ResponsiveTableWrap,
    EmptyResultsRow,
} from "../admin/examRelated/resultsUi";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";

function subjectColumnsFromResults(results) {
    const map = new Map();
    for (const row of results || []) {
        for (const subject of row.subjects || []) {
            const key = subject.subjectId || subject.subjectName;
            if (!key || map.has(key)) continue;
            map.set(key, {
                key,
                name: subject.subjectName || "Subject",
            });
        }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function gradeColor(gradeInfo) {
    if (!gradeInfo?.passed) return "error.main";
    if (gradeInfo.color === "warning") return "warning.main";
    if (gradeInfo.color === "info") return "info.main";
    return "success.main";
}

const TeacherClassResults = () => {
    const { homeroomClassId } = useFormTeacherHomeroom();
    const { activeClassId: teachClassId } = useTeacherTeaching();
    const classId = homeroomClassId || teachClassId;

    const [loading, setLoading] = useState(true);
    const [classData, setClassData] = useState(null);
    const [results, setResults] = useState([]);
    const [publishedLabel, setPublishedLabel] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!classId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const classRes = await axios.get(`${getApiBaseUrl()}/Sclass/${classId}`);
                if (cancelled) return;
                setClassData(classRes.data);

                const params = new URLSearchParams({ publishedOnly: "true" });
                let periodLabel = "";
                try {
                    const { data: periods } = await axios.get(
                        `${getApiBaseUrl()}/ClassResults/periods/${classId}`,
                    );
                    const latest = Array.isArray(periods) ? periods[0] : null;
                    if (latest?.term) params.set("term", String(latest.term));
                    if (latest?.academicYear) params.set("academicYear", String(latest.academicYear));
                    if (latest?.reportPeriod) params.set("reportPeriod", String(latest.reportPeriod));
                    periodLabel = [latest?.term, latest?.academicYear, latest?.reportPeriod]
                        .filter(Boolean)
                        .join(" · ");
                } catch {
                    /* use published results without period scope */
                }

                const resultsRes = await axios.get(
                    `${getApiBaseUrl()}/ClassResults/${classId}?${params.toString()}`,
                );
                if (!cancelled) {
                    setResults(Array.isArray(resultsRes.data) ? resultsRes.data : []);
                    setPublishedLabel(periodLabel);
                    setError("");
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.message || "Could not load class results");
                    setResults([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [classId]);

    const educationLevel = classData?.educationLevel || "Secondary";
    const levelCode = classData?.levelCode || "";
    const sclassName = classData?.sclassName || "";

    const subjectColumns = useMemo(() => subjectColumnsFromResults(results), [results]);

    const subjectsOfferedCount = subjectColumns.length;

    const summary = useMemo(() => {
        const total = results.length;
        const evaluable = results.filter((r) => {
            const e = evaluateTermClassPass(
                r.subjects,
                educationLevel,
                levelCode,
                sclassName,
                subjectsOfferedCount,
            );
            return !e.incomplete && e.passed != null;
        });
        const passed = evaluable.filter((r) =>
            evaluateTermClassPass(
                r.subjects,
                educationLevel,
                levelCode,
                sclassName,
                subjectsOfferedCount,
            ).passed,
        ).length;
        const topStudent = results.find((r) => Number(r.position) === 1);
        return {
            total,
            passRate: evaluable.length ? ((passed / evaluable.length) * 100).toFixed(1) : 0,
            topStudent: topStudent?.name || "—",
        };
    }, [results, educationLevel, levelCode, sclassName, subjectsOfferedCount]);

    const passRuleText = useMemo(
        () => classPassRuleSummary(educationLevel, levelCode, sclassName),
        [educationLevel, levelCode, sclassName],
    );

    if (!classId && !loading) {
        return (
            <ResultsPageContainer>
                <Alert severity="info">
                    Select a class from My subjects, or ask your director to assign you as form teacher on a class.
                </Alert>
            </ResultsPageContainer>
        );
    }

    return (
        <ResultsPageContainer>
            <ResultsPageHeader
                eyebrow="Teacher"
                title={`Class Results — ${classData?.sclassName || ""}`}
                subtitle={
                    publishedLabel
                        ? `Director-approved marks for ${publishedLabel}. ${passRuleText}`
                        : passRuleText
                }
            />

            {error ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <KpiCard title="Students" value={summary.total} loading={loading} color="primary" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <KpiCard
                        title="Pass rate (≥50% avg)"
                        value={`${summary.passRate}%`}
                        loading={loading}
                        color="success"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <KpiCard title="#1 in class" value={summary.topStudent} loading={loading} color="warning" />
                </Grid>
            </Grid>

            <ResultsSectionPaper title="Student results by subject">
                {loading ? (
                    <Skeleton height={280} />
                ) : (
                    <ResponsiveTableWrap>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Pos</TableCell>
                                    <TableCell>Student</TableCell>
                                    <TableCell>Roll</TableCell>
                                    {subjectColumns.map((column) => (
                                        <TableCell key={column.key} align="right">
                                            {column.name}
                                        </TableCell>
                                    ))}
                                    <TableCell align="right">Average</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Report</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {results.length === 0 ? (
                                    <EmptyResultsRow
                                        colSpan={subjectColumns.length + 6}
                                        message="No published results yet. Marks appear here after the director approves the class package."
                                    />
                                ) : (
                                    results.map((row) => {
                                        const termPass = evaluateTermClassPass(
                                            row.subjects,
                                            educationLevel,
                                            levelCode,
                                            sclassName,
                                            subjectsOfferedCount,
                                        );
                                        return (
                                            <TableRow key={row.studentId} hover>
                                                <TableCell>{row.position ?? "—"}</TableCell>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell>{row.rollNum ?? "—"}</TableCell>
                                                {subjectColumns.map((column) => {
                                                    const subject = (row.subjects || []).find(
                                                        (item) =>
                                                            String(item.subjectId || item.subjectName) ===
                                                            String(column.key),
                                                    );
                                                    if (!subject) {
                                                        return (
                                                            <TableCell key={`${row.studentId}-${column.key}`} align="right">
                                                                —
                                                            </TableCell>
                                                        );
                                                    }
                                                    const gradeInfo = getMalawiGrade(
                                                        subject.marksObtained,
                                                        educationLevel,
                                                        levelCode,
                                                        sclassName,
                                                    );
                                                    return (
                                                        <TableCell
                                                            key={`${row.studentId}-${column.key}`}
                                                            align="right"
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={600}
                                                                sx={{ color: gradeColor(gradeInfo) }}
                                                            >
                                                                {Number(subject.marksObtained)} / {gradeInfo.grade}
                                                            </Typography>
                                                        </TableCell>
                                                    );
                                                })}
                                                <TableCell align="right">
                                                    {termPass.average != null
                                                        ? `${termPass.average.toFixed(1)}%`
                                                        : "—"}
                                                </TableCell>
                                                <TableCell>
                                                    {termPass.incomplete ? (
                                                        <Chip
                                                            size="small"
                                                            label="Incomplete"
                                                            variant="outlined"
                                                            title={termPass.reason}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            label={termPass.passed ? "Pass" : "Fail"}
                                                            size="small"
                                                            color={termPass.passed ? "success" : "error"}
                                                            title={termPass.reason}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        component={RouterLink}
                                                        to={`/Teacher/report-card/${row.studentId}`}
                                                        startIcon={<ArticleOutlinedIcon />}
                                                    >
                                                        Card
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </ResponsiveTableWrap>
                )}
            </ResultsSectionPaper>

            {results.length > 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                    {passRuleText} Position uses the same subject average for class ranking. Individual subject grades
                    show the marking scale only — overall Pass/Fail uses the average rule above.
                </Typography>
            ) : null}
        </ResultsPageContainer>
    );
};

export default TeacherClassResults;
